import { useEffect, useMemo, useState } from "react";
import PageHero from "../../components/PageHero.jsx";
import { fetchPublicStreamers } from "../../lib/publicDbApi.js";

const TWITCH_ICON = "https://racevision.info/assets/twitch.png";
const YOUTUBE_ICON = "https://racevision.info/assets/youtube.png";
const STATIC_STREAMERS = [
    {
        season_driver_id: "static-racevisiontv",
        driver_name: "offizieller Liga Stream",
        stream_url: "https://www.youtube.com/@RacevisionTV",
        team_name: "RaceVision",
    },
    {
        season_driver_id: "static-nolimit",
        driver_name: "NoLimit Broadcast",
        stream_url: "https://www.youtube.com/@nolimitmotorsporttv",
        team_name: "NoLimit Motorsport",
    },
    {
        season_driver_id: "static-kimivf",
        driver_name: "Patrick Valente Ferro",
        stream_url: "https://www.twitch.tv/kimivf",
        team_name: "Flying Arrow Racing",
    },
];

function normalizeUrl(value) {
    const str = String(value ?? "").trim();
    if (!str) return "";
    if (/^https?:\/\//i.test(str)) return str;
    return `https://${str.replace(/^https?:\/\//i, "")}`;
}

function getChannelNameFromUrl(url) {
    const href = normalizeUrl(url);
    if (!href) return "";
    try {
        const u = new URL(href);
        const parts = u.pathname.split("/").filter(Boolean);
        if (!parts.length) return u.hostname.replace(/^www\./i, "");
        return parts[parts.length - 1];
    } catch {
        const clean = href.split("?")[0].replace(/\/+$/, "");
        const parts = clean.split("/").filter(Boolean);
        return parts.length ? parts[parts.length - 1] : clean;
    }
}

function getPlatform(url) {
    const href = normalizeUrl(url).toLowerCase();
    if (href.includes("twitch.tv")) return "twitch";
    if (href.includes("youtube.com") || href.includes("youtu.be")) return "youtube";
    return "";
}

function platformLabel(platform) {
    if (platform === "twitch") return "Twitch";
    if (platform === "youtube") return "YouTube";
    return "Stream";
}

function platformIcon(platform) {
    if (platform === "twitch") return TWITCH_ICON;
    if (platform === "youtube") return YOUTUBE_ICON;
    return "";
}

function renderChannelLink(value) {
    const href = normalizeUrl(value);
    if (!href) return null;

    const platform = getPlatform(href);
    const channelName = getChannelNameFromUrl(href) || href;
    const iconSrc = platformIcon(platform);

    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            title={href}
            className="button button--secondary"
            style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                width: "fit-content",
            }}
        >
            {iconSrc ? (
                <img
                    src={iconSrc}
                    alt={platformLabel(platform)}
                    style={{
                        height: platform === "twitch" ? 24 : 22,
                        width: platform === "twitch" ? 24 : 22,
                        objectFit: "contain",
                        display: "block",
                        flexShrink: 0,
                    }}
                />
            ) : null}
            <span>{channelName}</span>
        </a>
    );
}

function StreamerCard({ driverName, link, teamName }) {
    const href = normalizeUrl(link);
    const platform = getPlatform(href);
    const channelName = getChannelNameFromUrl(href) || "Channel";
    const iconSrc = platformIcon(platform);

    return (
        <article className="rv-modern-card rv-modern-card--accent">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                    marginBottom: 12,
                }}
            >
                <div>
                    <div className="rv-section-kicker">Live Channel</div>
                    <h3 style={{ fontSize: 24, marginBottom: 4 }}>{driverName || "Unbekannter Fahrer"}</h3>
                    <p className="rv-modern-muted" style={{ margin: 0 }}>
                        {teamName || "Kein Team hinterlegt"}
                    </p>
                </div>

                {iconSrc ? (
                    <div
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 14,
                            display: "grid",
                            placeItems: "center",
                            background: "rgba(255,255,255,.055)",
                            border: "1px solid rgba(255,255,255,.10)",
                        }}
                    >
                        <img
                            src={iconSrc}
                            alt=""
                            aria-hidden="true"
                            style={{ width: 28, height: 28, objectFit: "contain" }}
                        />
                    </div>
                ) : (
                    <span className="rv-status-pill rv-status-pill--info">Live</span>
                )}
            </div>

            <div className="rv-info-grid" style={{ marginBottom: 14 }}>
                <div className="rv-info-tile">
                    <div className="rv-info-tile__label">Channel</div>
                    <div className="rv-info-tile__value">{channelName}</div>
                </div>
            </div>

            {renderChannelLink(link)}
        </article>
    );
}

export default function Streamer() {
    const [rows, setRows] = useState([]);
    const [seasonName, setSeasonName] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                setLoading(true);
                setError("");
                const res = await fetchPublicStreamers({ seriesKey: "gt3x", seasonKey: "active" });
                if (cancelled) return;
                setRows(Array.isArray(res?.rows) ? res.rows : []);
                setSeasonName(res?.season?.display_name || "");
            } catch (e) {
                if (!cancelled) {
                    const message = String(e?.message || "");
                    // Backend endpoint noch nicht deployed: statische Streamer trotzdem zeigen.
                    if (message.toLowerCase().includes("not found") || message.includes("404")) {
                        setRows([]);
                        setSeasonName("");
                        setError("");
                    } else {
                        setError(message || "Fehler beim Laden der Streamer-Daten.");
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const stats = useMemo(() => {
        const merged = [...STATIC_STREAMERS, ...rows];
        const twitch = merged.filter((entry) => getPlatform(entry?.stream_url) === "twitch").length;
        const youtube = merged.filter((entry) => getPlatform(entry?.stream_url) === "youtube").length;
        return { total: merged.length, twitch, youtube };
    }, [rows]);

    const displayRows = useMemo(() => {
        const merged = [...STATIC_STREAMERS, ...rows];
        const seen = new Set();
        return merged.filter((entry) => {
            const key = normalizeUrl(entry?.stream_url).toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [rows]);

    return (
        <div className="rv-page">
            <PageHero kicker="GT3 Xperience" title="Streamer">
                Hier findest du Stream-Links von aktiven Fahrern der aktuellen Saison.
                Perfekt, um Trainings, Rennen oder Onboards live mitzuverfolgen.
            </PageHero>

            <section className="rv-modern-card">
                <div className="rv-section-head">
                    <div>
                        <div className="rv-section-kicker">Broadcast</div>
                        <h2>Live Channels{seasonName ? ` / ${seasonName}` : ""}</h2>
                    </div>

                    <div className="rv-pill-row">
                        <span className="rv-status-pill rv-status-pill--info">Gesamt: {stats.total}</span>
                        <span className="rv-status-pill rv-status-pill--warn">Twitch: {stats.twitch}</span>
                        <span className="rv-status-pill rv-status-pill--ok">YouTube: {stats.youtube}</span>
                    </div>
                </div>

                {loading && <p className="rv-modern-muted">Lade Streamer-Liste ...</p>}
                {!loading && error && <p style={{ color: "var(--err)" }}>{error}</p>}

                {!loading && !error && displayRows.length === 0 && (
                    <div
                        style={{
                            textAlign: "center",
                            padding: 28,
                            borderRadius: 18,
                            background: "rgba(255,255,255,.035)",
                            border: "1px solid rgba(255,255,255,.08)",
                        }}
                    >
                        <h2>Noch keine Streamer eingetragen</h2>
                        <p className="rv-modern-muted">
                            Sobald aktive Saisonfahrer einen Stream-Link haben, erscheinen sie hier.
                        </p>
                    </div>
                )}

                {!loading && !error && displayRows.length > 0 && (
                    <div className="rv-modern-grid">
                        {displayRows.map((entry) => (
                            <StreamerCard
                                key={`${entry.season_driver_id}-${entry.stream_url}`}
                                driverName={entry.driver_name}
                                link={entry.stream_url}
                                teamName={entry.team_name}
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
